import {
    MAP_TARIFF_ID_TO_SALESCHANNEL,
    RedplusTariff,
    RedTariff,
    SAILS_BNT_STORAGE,
    SAILS_PARAM_SUB_ID,
    SAILS_REDPLUS_STORAGE,
    SalesChannel,
    SALESCHANNEL_CONSUMER,
    SALESCHANNEL_REDPLUS,
    URL_PARAM_SUBGROUP_ID,
    YoungTariff,
    ADDITIONAL_PAGE_OPTIONS,
    FAMILYCARD_S_VIRTUAL_ID,
    MAP_SALESCHANNEL_TO_VIRTUAL_ID,
} from '@vfde-sails/constants';
import { getQueryParam } from '@vfde-sails/utils';
import {
    getSalesChannelFromSails,
    getSessionStorageItemJson,
    getSubscriptionIdFromSails,
    RedPlusStorage,
} from '@vfde-sails/storage';
import {
    APP,
    CONTAINER_TARIFF,
} from '../../../app/constants';
import { getSalesChannelFromBackup } from './getSalesChannel';
import { SailsDeviceDetailsStorage } from '../../../interfaces/storage';

/**
 * Get the subscription ID from URL or session storage and validate against saleschannel
 */
export const getSubscriptionId = (
    salesChannel: SalesChannel = SALESCHANNEL_CONSUMER,
): RedTariff | YoungTariff | RedplusTariff | null => {
    // Default subscription id
    let subscriptionId = getSubscriptionFromUrl(salesChannel) ?? getSubscriptionFromStorage();

    // Redplus subscription id
    if (salesChannel === SALESCHANNEL_REDPLUS) {
        subscriptionId = getSubscriptionFromUrl(SALESCHANNEL_REDPLUS)
            ?? getSubscriptionFromRedplusStorage()
            ?? getSubscriptionFromStorage();
    }

    if (subscriptionId && validateSubscriptionId(subscriptionId, salesChannel!)) {
        return subscriptionId;
    }

    // For redplus: if no valid subscriptionId, or subscriptionId is not redplus, we return redplus allnet as default
    if (salesChannel === SALESCHANNEL_REDPLUS) {
        return window[ADDITIONAL_PAGE_OPTIONS].redplus.defaultSubscriptionId || FAMILYCARD_S_VIRTUAL_ID;
    }

    return null;
};

/**
 * Get subscription ID from URL
 */
export const getSubscriptionFromUrl = (salesChannel?: SalesChannel): RedTariff | YoungTariff | RedplusTariff | null => {
    const tariffIdFromUrlParam = getQueryParam(URL_PARAM_SUBGROUP_ID) as RedTariff | YoungTariff | RedplusTariff;
    const isValidTariffId = !!tariffIdFromUrlParam && tariffIdFromUrlParam in MAP_TARIFF_ID_TO_SALESCHANNEL;

    if (
        salesChannel
        && isValidTariffId
        && MAP_TARIFF_ID_TO_SALESCHANNEL[tariffIdFromUrlParam].includes(salesChannel)
    ) {
        return tariffIdFromUrlParam;
    }

    if (!salesChannel && isValidTariffId) {
        return tariffIdFromUrlParam;
    }

    return null;
};

/**
 * Get subscription ID from storage
 */
export const getSubscriptionFromStorage = (): RedTariff | YoungTariff | RedplusTariff | undefined => {
    const storage = getSessionStorageItemJson<SailsDeviceDetailsStorage | null>(SAILS_BNT_STORAGE);

    if (!storage) {
        return undefined;
    }

    return getSubscriptionFromBackup(storage) ?? getSubscriptionIdFromSails(storage, getSalesChannelFromSails(storage));
};

const getSubscriptionFromBackup = (storage: SailsDeviceDetailsStorage | null): RedTariff | YoungTariff | RedplusTariff | undefined => {
    const salesChannelFromBackup = getSalesChannelFromBackup(storage);

    if (!salesChannelFromBackup) {
        return;
    }

    return storage?.[APP]?.[CONTAINER_TARIFF]?.[SAILS_PARAM_SUB_ID]?.[salesChannelFromBackup];
};

/**
 * Get Redplus subscription ID from storage
 */
export const getSubscriptionFromRedplusStorage = (): RedplusTariff | undefined => {
    const storage = getSessionStorageItemJson<RedPlusStorage | null>(SAILS_REDPLUS_STORAGE);

    return storage?.[SAILS_PARAM_SUB_ID]?.toString() as RedplusTariff;
};

/**
 * Validate subscription ID
 */
export const validateSubscriptionId = (
    id: RedTariff | YoungTariff | RedplusTariff,
    salesChannel: SalesChannel,
): boolean =>
    MAP_SALESCHANNEL_TO_VIRTUAL_ID[salesChannel].includes(id);
